import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDashboard } from '../store/useDashboard';
import api from '../services/api';
import { demandeService } from '../services/api';
import {
  IconeListe, IconeNote, IconeCrayon, IconeDocument, IconeAvion, IconeRecu,
  IconeRafraichir, IconeDossier, IconeHorloge, IconeArchive, IconeAccueil,
  IconeMonnaie, IconeValide, IconeFeu, IconeTrombone,
} from '../components/IconesSVG';

const TYPES_LABEL = {
  expression_besoin: { label: 'Fiches EB',         icon: IconeListe, color: '#2980B9' },
  bon_commande:      { label: 'Bons de commande',   icon: IconeNote, color: '#8E44AD' },
  memo_filiale:      { label: 'Memos Filiale',      icon: IconeCrayon, color: '#27AE60' },
  memo_holding:      { label: 'Memos Holding',      icon: IconeDocument, color: '#C0392B' },
  frais_mission:     { label: 'Frais de mission',   icon: IconeAvion, color: '#E67E22' },
  facture:           { label: 'Factures',           icon: IconeRecu, color: '#16A085' },
  regularisation:    { label: 'Regularisations',    icon: IconeRafraichir, color: '#935116' },
};

const STATUTS_LABEL = {
  approuve:                   { label: 'Approuve',        color: '#1E8449', bg: '#EAFAF1' },
  en_attente_pdg:             { label: 'Attente PDG',     color: '#1A5E8A', bg: '#EAF4FB' },
  en_validation_dg_filiale:   { label: 'Chez DG',         color: '#1A5E8A', bg: '#EAF4FB' },
  rejete:                     { label: 'Rejete',          color: '#96281B', bg: '#FDEDEC' },
  correction_requise:         { label: 'Correction',      color: '#96281B', bg: '#FDEDEC' },
  a_regulariser:              { label: 'A regulariser',   color: '#935116', bg: '#FDF2E9' },
  soumis:                     { label: 'En cours',        color: '#9A7D0A', bg: '#FEF9E7' },
};

export default function ClassementPage() {
  const navigate = useNavigate();
  const { allerDashboard } = useDashboard();
  const [classement, setClassement] = useState([]);
  const [loading, setLoading] = useState(true);
  const [anneeOuverte, setAnneeOuverte] = useState(null);
  const [filialeOuverte, setFilialeOuverte] = useState(null);
  const [typeOuvert, setTypeOuvert] = useState(null);
  const [demandes, setDemandes] = useState([]);
  const [loadingDemandes, setLoadingDemandes] = useState(false);
  const [contexteActuel, setContexteActuel] = useState(null);
  const [searchDoc, setSearchDoc] = useState('');

  useEffect(() => { charger(); }, []);

  const charger = async () => {
    try {
      setLoading(true);
      const res = await api.get('/demandes/classement');
      setClassement(res.data || []);
      const annees = [...new Set((res.data || []).map(r => r._id.annee))].sort((a, b) => b - a);
      if (annees.length > 0) setAnneeOuverte(String(annees[0]));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const chargerDemandes = async (annee, filialeId, type) => {
    setLoadingDemandes(true);
    setDemandes([]);
    try {
      const res = await demandeService.rechercher({
        type,
        filiale: filialeId,
        dateDebut: annee + '-01-01',
        dateFin: annee + '-12-31',
      });
      setDemandes(res.data || []);
    } catch (err) { console.error(err); }
    finally { setLoadingDemandes(false); }
  };

  const ouvrirType = (annee, filialeId, filiale, type) => {
    const key = annee + '_' + filialeId + '_' + type;
    if (typeOuvert === key) {
      setTypeOuvert(null); setDemandes([]); setContexteActuel(null);
    } else {
      setTypeOuvert(key);
      setContexteActuel({ annee, filiale, type });
      chargerDemandes(annee, filialeId, type);
      setSearchDoc('');
    }
  };

  // Arbre
  const arbre = {};
  classement.forEach(item => {
    const { annee, filiale: filialeId, type } = item._id;
    const filialeInfo = item.filialeInfo?.[0];
    const filialeKey = filialeId?.toString() || 'inconnu';
    if (!arbre[annee]) arbre[annee] = {};
    if (!arbre[annee][filialeKey]) arbre[annee][filialeKey] = {
      nom: filialeInfo?.nom || 'Inconnu',
      code: filialeInfo?.code || '?',
      id: filialeId, types: {}
    };
    if (!arbre[annee][filialeKey].types[type]) arbre[annee][filialeKey].types[type] = { count: 0, montant: 0 };
    arbre[annee][filialeKey].types[type].count += item.count;
    arbre[annee][filialeKey].types[type].montant += item.montantTotal || 0;
  });

  const annees = Object.keys(arbre).sort((a, b) => b - a);
  const formatMontant = (m) => m ? Number(m).toLocaleString('fr-FR') + ' FCFA' : '—';
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
  const totalGlobal = classement.reduce((s, r) => s + r.count, 0);
  const montantGlobal = classement.reduce((s, r) => s + (r.montantTotal || 0), 0);

  const demandesFiltrees = demandes.filter(d =>
    !searchDoc ||
    d.reference?.toLowerCase().includes(searchDoc.toLowerCase()) ||
    d.motif?.toLowerCase().includes(searchDoc.toLowerCase())
  );

  // Stats globales par type
  const statsParType = {};
  classement.forEach(item => {
    const type = item._id.type;
    if (!statsParType[type]) statsParType[type] = 0;
    statsParType[type] += item.count;
  });

  return (
    <div style={s.page}>
      {/* HEADER */}
      <div style={s.header}>
        <button onClick={() => allerDashboard()} style={s.backBtn}>← Retour</button>
        <div style={{ flex: 1 }}>
          <div style={s.pageTitle}><IconeDossier taille={16} /> Plan de classement GED</div>
          <div style={s.pageSub}>
            {totalGlobal} documents · {formatMontant(montantGlobal)} total
          </div>
        </div>
        <button onClick={() => navigate('/nouvelle-demande')} style={s.btnNew}>
          + Nouvelle demande
        </button>
      </div>

      {/* STATS GLOBALES */}
      <div style={s.statsBar}>
        {Object.entries(statsParType).slice(0, 5).map(([type, count]) => {
          const info = TYPES_LABEL[type] || { label: type, icon: IconeDocument, color: '#666' };
          return (
            <div key={type} style={s.statPill}>
              <span style={{ display: 'inline-flex' }}><info.icon taille={16} /></span>
              <span style={{ fontSize: 12, color: '#555' }}>{info.label}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: info.color }}>{count}</span>
            </div>
          );
        })}
      </div>

      <div style={s.body}>
        {loading ? (
          <div style={s.empty}>
            <div style={{ marginBottom: 12, color: '#bbb' }}><IconeHorloge taille={36} /></div>
            Chargement du plan de classement...
          </div>
        ) : annees.length === 0 ? (
          <div style={s.empty}>
            <div style={{ marginBottom: 14, color: '#ccc' }}><IconeDossier taille={56} /></div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#0D1B2A', marginBottom: 6 }}>Aucun document classe</div>
            <div style={{ fontSize: 13, color: '#888' }}>Soumettez votre premiere demande pour commencer</div>
            <button onClick={() => navigate('/nouvelle-demande')} style={{ ...s.btnNew, marginTop: 16 }}>
              + Nouvelle demande
            </button>
          </div>
        ) : (
          <div style={s.layout}>
            {/* ARBORESCENCE */}
            <div style={s.arbre}>
              <div style={s.arbreTitre}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconeArchive taille={14} /> Structure documentaire</span>
                <span style={{ fontSize: 10, background: '#e5e7eb', padding: '2px 8px', borderRadius: 10, fontWeight: 600 }}>{totalGlobal}</span>
              </div>

              {annees.map(annee => {
                const filialesAnnee = arbre[annee];
                const totalAnnee = Object.values(filialesAnnee).reduce((s, f) =>
                  s + Object.values(f.types).reduce((ss, t) => ss + t.count, 0), 0);
                const isAnneeOpen = anneeOuverte === annee;

                return (
                  <div key={annee} style={s.noeudAnnee}>
                    {/* ANNEE */}
                    <div style={{ ...s.noeudBtn, ...(isAnneeOpen ? s.noeudBtnAnnee : {}) }}
                      onClick={() => {
                        setAnneeOuverte(isAnneeOpen ? null : annee);
                        setFilialeOuverte(null); setTypeOuvert(null); setDemandes([]);
                      }}>
                      <div style={{ ...s.noeudIconBox, background: isAnneeOpen ? '#C0392B' : '#0D1B2A' }}>
                        <IconeDossier taille={15} />
                      </div>
                      <span style={{ ...s.noeudLabel, fontWeight: 700, fontSize: 14 }}>{annee}</span>
                      <span style={{ ...s.noeudBadge, background: isAnneeOpen ? 'rgba(192,57,43,0.15)' : '#e5e7eb', color: isAnneeOpen ? '#C0392B' : '#555' }}>{totalAnnee}</span>
                    </div>

                    {/* FILIALES */}
                    {isAnneeOpen && Object.entries(filialesAnnee).map(([filialeKey, filiale]) => {
                      const totalFiliale = Object.values(filiale.types).reduce((s, t) => s + t.count, 0);
                      const isFilialeOpen = filialeOuverte === annee + '_' + filialeKey;

                      return (
                        <div key={filialeKey}>
                          <div style={{ ...s.noeudBtn, paddingLeft: 24, ...(isFilialeOpen ? s.noeudBtnFiliale : {}) }}
                            onClick={() => {
                              setFilialeOuverte(isFilialeOpen ? null : annee + '_' + filialeKey);
                              setTypeOuvert(null); setDemandes([]);
                            }}>
                            <div style={{ width: 2, alignSelf: 'stretch', background: '#E5E7EB', marginRight: 8, flexShrink: 0 }} />
                            <div style={{ ...s.noeudIconBox, background: isFilialeOpen ? '#2980B9' : '#8899AA', fontSize: 11 }}>
                              {filiale.code}
                            </div>
                            <span style={{ ...s.noeudLabel, fontWeight: 500 }}>{filiale.nom}</span>
                            <span style={{ ...s.noeudBadge, background: isFilialeOpen ? 'rgba(41,128,185,0.15)' : '#e5e7eb', color: isFilialeOpen ? '#2980B9' : '#555' }}>{totalFiliale}</span>
                          </div>

                          {/* TYPES */}
                          {isFilialeOpen && Object.entries(filiale.types).map(([type, typeData]) => {
                            const typeInfo = TYPES_LABEL[type] || { label: type, icon: IconeDocument, color: '#666' };
                            const key = annee + '_' + filialeKey + '_' + type;
                            const isTypeOpen = typeOuvert === key;

                            return (
                              <div key={type}>
                                <div style={{ ...s.noeudBtn, paddingLeft: 42, background: isTypeOpen ? typeInfo.color + '10' : 'transparent', borderLeft: isTypeOpen ? `3px solid ${typeInfo.color}` : '3px solid transparent' }}
                                  onClick={() => ouvrirType(annee, filiale.id, filiale, type)}>
                                  <div style={{ width: 2, alignSelf: 'stretch', background: '#E5E7EB', marginRight: 8, flexShrink: 0 }} />
                                  <span style={{ display: 'inline-flex' }}><typeInfo.icon taille={16} /></span>
                                  <div style={{ flex: 1 }}>
                                    <div style={{ ...s.noeudLabel, fontSize: 12, color: isTypeOpen ? typeInfo.color : '#555' }}>{typeInfo.label}</div>
                                    <div style={{ fontSize: 10, color: '#aaa' }}>{formatMontant(typeData.montant)}</div>
                                  </div>
                                  <span style={{ ...s.noeudBadge, background: typeInfo.color + '20', color: typeInfo.color }}>{typeData.count}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>

            {/* PANNEAU DOCUMENTS */}
            <div style={s.panneau}>
              {!contexteActuel ? (
                <div style={s.panneauVide}>
                  <div style={{ marginBottom: 16, opacity: 0.3 }}><IconeDossier taille={64} /></div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#0D1B2A', marginBottom: 8 }}>
                    Selectionnez un dossier
                  </div>
                  <div style={{ fontSize: 13, color: '#888', lineHeight: 1.6, maxWidth: 260, textAlign: 'center' }}>
                    Naviguez dans l arborescence et cliquez sur un type de document pour voir son contenu.
                  </div>
                  {/* Mini guide */}
                  <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 8, width: '100%', maxWidth: 280 }}>
                    {[
                      { icon: IconeDossier, label: 'Annee', desc: 'Cliquez pour ouvrir' },
                      { icon: IconeDossier, label: 'Filiale', desc: 'Puis la filiale' },
                      { icon: IconeListe, label: 'Type', desc: 'Puis le type de document' },
                    ].map((step, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#F9FAFB', borderRadius: 8, border: '0.5px solid #e5e7eb' }}>
                        <span style={{ display: 'inline-flex', color: '#888' }}><step.icon taille={18} /></span>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#0D1B2A' }}>{step.label}</div>
                          <div style={{ fontSize: 11, color: '#888' }}>{step.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {/* Fil d'ariane */}
                  <div style={s.breadcrumb}>
                    <span style={s.breadcrumbItem} onClick={() => allerDashboard()}><IconeAccueil taille={14} /></span>
                    <span style={s.breadcrumbSep}>›</span>
                    <span style={s.breadcrumbItem}>{contexteActuel.annee}</span>
                    <span style={s.breadcrumbSep}>›</span>
                    <span style={{ ...s.breadcrumbItem, background: '#0D1B2A', color: '#fff', padding: '2px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600 }}>
                      {contexteActuel.filiale?.code}
                    </span>
                    <span style={s.breadcrumbSep}>›</span>
                    <span style={{ fontSize: 12, color: TYPES_LABEL[contexteActuel.type]?.color || '#555', fontWeight: 600 }}>
                      {TYPES_LABEL[contexteActuel.type]?.icon} {TYPES_LABEL[contexteActuel.type]?.label}
                    </span>
                  </div>

                  {/* Stats dossier */}
                  <div style={s.dossierStats}>
                    {[
                      { label: 'Documents', value: demandes.length, color: '#0D1B2A', icon: IconeDocument },
                      { label: 'FCFA total', value: Number(demandes.reduce((s, d) => s + (d.montantEstime || 0), 0)).toLocaleString('fr-FR'), color: '#C0392B', icon: IconeMonnaie },
                      { label: 'Approuves', value: demandes.filter(d => d.statut === 'approuve').length, color: '#1E8449', icon: IconeValide },
                      { label: 'En cours', value: demandes.filter(d => !['approuve', 'rejete'].includes(d.statut)).length, color: '#9A7D0A', icon: IconeHorloge },
                    ].map((st, i) => (
                      <div key={i} style={s.dossierStat}>
                        <div style={{ marginBottom: 4, color: st.color }}><st.icon taille={16} /></div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: st.color }}>{st.value}</div>
                        <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>{st.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Recherche dans dossier */}
                  {demandes.length > 5 && (
                    <div style={{ padding: '10px 14px', borderBottom: '0.5px solid #e5e7eb' }}>
                      <input
                        style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '0.5px solid #ddd', fontSize: 12, fontFamily: 'inherit', outline: 'none', background: '#F9FAFB', boxSizing: 'border-box' }}
                        placeholder="Rechercher dans ce dossier..."
                        value={searchDoc}
                        onChange={e => setSearchDoc(e.target.value)}
                      />
                    </div>
                  )}

                  {/* Liste documents */}
                  {loadingDemandes ? (
                    <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>
                      <div style={{ marginBottom: 8, color: '#bbb' }}><IconeHorloge taille={28} /></div>
                      Chargement...
                    </div>
                  ) : demandesFiltrees.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 40, color: '#888' }}>
                      <div style={{ marginBottom: 8, color: '#ccc' }}><IconeDossier taille={36} /></div>
                      {searchDoc ? 'Aucun resultat pour cette recherche' : 'Aucun document dans ce dossier'}
                    </div>
                  ) : (
                    <div style={s.docsList}>
                      {demandesFiltrees.map(d => {
                        const statutInfo = STATUTS_LABEL[d.statut] || { label: d.statut, color: '#666', bg: '#F0F0F0' };
                        const typeInfo = TYPES_LABEL[d.type] || { icon: IconeDocument, color: '#666' };
                        return (
                          <div key={d._id} style={s.docItem} onClick={() => navigate('/demandes/' + d._id)}>
                            {/* Icone type */}
                            <div style={{ width: 40, height: 40, borderRadius: 10, background: typeInfo.color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0, border: `1px solid ${typeInfo.color}25` }}>
                              <typeInfo.icon taille={18} />
                            </div>

                            {/* Infos */}
                            <div style={{ flex: 1, overflow: 'hidden' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                                <span style={{ fontSize: 13, fontWeight: 600, color: '#0D1B2A' }}>{d.reference}</span>
                                {d.urgence && <span style={{ fontSize: 9, background: '#FDEDEC', color: '#96281B', padding: '1px 6px', borderRadius: 8, fontWeight: 600 }}><IconeFeu taille={10} /></span>}
                              </div>
                              <div style={{ fontSize: 12, color: '#555', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: 3 }}>{d.motif}</div>
                              <div style={{ fontSize: 10, color: '#aaa', display: 'flex', gap: 8 }}>
                                <span>{d.demandeur?.prenom} {d.demandeur?.nom}</span>
                                <span>·</span>
                                <span>{formatDate(d.createdAt)}</span>
                                {d.documents?.length > 0 && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>· <IconeTrombone taille={11} /> {d.documents.length}</span>}
                              </div>
                            </div>

                            {/* Droite */}
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5, flexShrink: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 700, color: '#C0392B' }}>
                                {d.montantEstime ? Number(d.montantEstime).toLocaleString('fr-FR') + ' F' : '—'}
                              </div>
                              <span style={{ fontSize: 10, padding: '3px 10px', borderRadius: 20, background: statutInfo.bg, color: statutInfo.color, fontWeight: 600 }}>
                                {statutInfo.label}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#F5F6FA', fontFamily: 'Inter, system-ui, sans-serif' },

  // HEADER
  header: { background: '#fff', borderBottom: '0.5px solid #e5e7eb', padding: '14px 24px', display: 'flex', alignItems: 'center', gap: 16 },
  backBtn: { background: 'transparent', border: '0.5px solid #ddd', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', flexShrink: 0 },
  pageTitle: { fontSize: 18, fontWeight: 700, color: '#0D1B2A', letterSpacing: '-0.3px' },
  pageSub: { fontSize: 12, color: '#888', marginTop: 2 },
  btnNew: { marginLeft: 'auto', padding: '10px 18px', borderRadius: 8, border: 'none', background: '#C0392B', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },

  // STATS BAR
  statsBar: { background: '#fff', borderBottom: '0.5px solid #e5e7eb', padding: '8px 24px', display: 'flex', gap: 8, overflowX: 'auto' },
  statPill: { display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 20, background: '#F5F6FA', border: '0.5px solid #e5e7eb', flexShrink: 0 },

  // BODY
  body: { padding: 24 },
  layout: { display: 'grid', gridTemplateColumns: '280px 1fr', gap: 20, alignItems: 'flex-start' },

  // ARBRE
  arbre: { background: '#fff', borderRadius: 12, border: '0.5px solid #e5e7eb', overflow: 'hidden', position: 'sticky', top: 24 },
  arbreTitre: { padding: '12px 16px', fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '0.5px solid #e5e7eb', background: '#F9FAFB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  noeudAnnee: { borderBottom: '0.5px solid #f0f0f0' },
  noeudBtn: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', cursor: 'pointer', color: '#0D1B2A', transition: '0.1s' },
  noeudBtnAnnee: { background: '#FDEDEC' },
  noeudBtnFiliale: { background: '#EAF4FB' },
  noeudIconBox: { width: 26, height: 26, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0, color: '#fff', fontWeight: 700 },
  noeudLabel: { flex: 1, fontSize: 13 },
  noeudBadge: { fontSize: 10, padding: '2px 7px', borderRadius: 10, fontWeight: 600, flexShrink: 0 },

  // PANNEAU
  panneau: { background: '#fff', borderRadius: 12, border: '0.5px solid #e5e7eb', minHeight: 500, overflow: 'hidden' },
  panneauVide: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 48, color: '#888', textAlign: 'center', minHeight: 500 },

  // BREADCRUMB
  breadcrumb: { display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', borderBottom: '0.5px solid #e5e7eb', background: '#F9FAFB', flexWrap: 'wrap' },
  breadcrumbItem: { fontSize: 12, color: '#888', cursor: 'pointer' },
  breadcrumbSep: { fontSize: 12, color: '#ccc' },

  // STATS DOSSIER
  dossierStats: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', borderBottom: '0.5px solid #e5e7eb' },
  dossierStat: { padding: '14px 16px', textAlign: 'center', borderRight: '0.5px solid #f0f0f0' },

  // DOCS
  docsList: { padding: 12, display: 'flex', flexDirection: 'column', gap: 8 },
  docItem: { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 10, border: '0.5px solid #e5e7eb', cursor: 'pointer', background: '#fff', transition: '0.1s' },

  // EMPTY
  empty: { textAlign: 'center', padding: 80, color: '#888', display: 'flex', flexDirection: 'column', alignItems: 'center' },
};
